<template>
  <div class="delivery-calendar-wrapper glass-theme fade-in-page">
    <!-- API Loading Overlay with Animation -->
    <transition name="loading-overlay">
      <div v-if="apiLoading" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">
                <i class="fas fa-calendar-alt"></i>
              </div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="loading-tips">
            <p class="tip-text">{{ currentTip }}</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Simple Calendar Header -->
    <div class="calendar-header">
      <h3>DELIVERY CALENDAR</h3>
      <div class="filters">
        <input 
          type="month" 
          v-model="selectedMonth" 
          @change="onMonthChange"
          class="month-picker-input"
        />
        <select 
          v-if="packet.userDetails && packet.userDetails.userrole === 'admin'" 
          v-model="location_id" 
          @change="onLocationChange"
          class="location-select"
        >
          <option v-for="opt in location_options" :key="opt.value" :value="opt.value">
            {{ opt.text }}
          </option>
        </select>
        <button @click="fetchDeliveryCounts" :disabled="apiLoading" class="refresh-btn">
          <span v-if="apiLoading" class="spinner-sm"></span>
          <span v-else>🔄</span> Refresh
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !apiLoading" class="loading">Loading...</div>

    <!-- Error -->
    <div v-else-if="apiError" class="error">
      <strong>Error:</strong> {{ apiError }}
    </div>

    <!-- Calendar with staggered animation -->
    <div v-else-if="deliveryData && calendarDays.length > 0" class="calendar">
      <div class="calendar-title">
        <div class="calendar-month-title">
          {{ formatMonthYear(selectedMonth) }}
        </div>
        <div class="calendar-location-title" v-if="location_id !== 90">
          {{ location_name }}
        </div>
      </div>
      
      <div class="calendar-row weekdays">
        <div>SUN</div>
        <div>MON</div>
        <div>TUE</div>
        <div>WED</div>
        <div>THU</div>
        <div>FRI</div>
        <div>SAT</div>
      </div>

      <div class="calendar-body">
        <div 
          v-for="(day, index) in calendarDays" 
          :key="day.date" 
          class="calendar-day fade-in-stagger" 
          :style="{ animationDelay: `${index * 0.02}s` }"
          :class="{ 
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'has-deliveries': day.deliveryCount > 0
          }"
          @click="day.deliveryCount > 0 && showDeliveryDetails(day)"
        >
          <div class="day-number">{{ day.day }}</div>
          
          <div v-if="day.deliveryCount > 0" class="day-stats">
            <div class="stat-row">🚚 Trips: {{ day.trips || 0 }}</div>
            <div class="stat-row">📦 Scheduled: {{ day.deliveryCount }}</div>
            <div class="stat-row">⚠️ Un-Assigned: {{ day.unassigned || 0 }}</div>
            <div class="stat-row">📍 Invalid: {{ day.invalidAddresses || 0 }}</div>
          </div>
          
          <div v-else class="no-deliveries">—</div>
        </div>
      </div>
    </div>

    <!-- No data state -->
    <div v-else-if="!loading && !deliveryData && !apiError" class="no-data">
      No delivery data available. Select a month and click Refresh.
    </div>

    <!-- Modal for details with truck animation -->
    <div v-if="selectedDay" class="modal-overlay" @click="closeModal">
      <div class="modal-content glass-modal slide-in-up" @click.stop>
        <div class="modal-header">
          <h4>{{ formatDate(selectedDay.date) }}</h4>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        
        <!-- Enhanced Truck Loading Animation with Parcels -->
        <div v-if="selectedDay.loadingOrders" class="truck-loading-container">
          <div class="truck-crossing-animation">
            <!-- Loading Dock / Warehouse -->
            <div class="loading-dock"></div>
            
            <!-- Parcels waiting on dock -->
            <div class="parcel-on-dock">📦</div>
            <div class="parcel-on-dock">📦</div>
            <div class="parcel-on-dock">📦</div>
            
            <!-- Flying parcels being loaded into truck -->
            <div class="flying-parcel flying-parcel-1">📦</div>
            <div class="flying-parcel flying-parcel-2">📦</div>
            <div class="flying-parcel flying-parcel-3">📦</div>
            
            <!-- Truck -->
            <div class="truck-moving">
              <div class="truck truck-facing-right">
                <div class="truck-body">
                  <!-- Parcels inside truck that appear after loading -->
                  <div class="parcel-inside parcel-inside-1">📦</div>
                  <div class="parcel-inside parcel-inside-2">📦</div>
                  <div class="parcel-inside parcel-inside-3">📦</div>
                </div>
                <div class="truck-cabin">
                  <div class="truck-window"></div>
                </div>
                <div class="truck-wheel wheel-front"></div>
                <div class="truck-wheel wheel-rear"></div>
                <div class="headlight"></div>
                <div class="exhaust"></div>
                <div class="exhaust"></div>
                <div class="exhaust"></div>
              </div>
            </div>
            
            <!-- Loading Text -->
            <div class="loading-text">
              <span class="loading-icon">🚚</span>
              Loading delivery orders...
              <div class="loading-dots">
                <span>.</span><span>.</span><span>.</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Error loading orders -->
        <div v-else-if="selectedDay.ordersError" class="error-orders">
          Error loading orders: {{ selectedDay.ordersError }}
        </div>
        
        <!-- Orders details with filters -->
        <div v-else-if="selectedDay.orders && selectedDay.orders.length > 0">
          <div class="filter-bar">
            <div class="filter-row">
              <div class="filter-group">
                <label>Invoice no</label>
                <input 
                  type="text" 
                  v-model="orderFilters.orderNo" 
                  placeholder="Search order..." 
                  class="filter-input"
                />
              </div>
              
              <div class="filter-group">
                <label>Requested Date Range</label>
                <div class="date-picker-container">
                  <input 
                    type="text" 
                    :id="'requested-date-picker-' + selectedDay.date"
                    class="filter-input date-picker-input"
                    :class="{ 'date-range-active': isRequestedDateRangeActive }"
                    placeholder="Select date range"
                    autocomplete="off"
                  />
                </div>
              </div>
              <div class="filter-group">
                <label>Schedule Date Range</label>
                <div class="date-picker-container">
                  <input 
                    type="text" 
                    :id="'schedule-date-picker-' + selectedDay.date"
                    class="filter-input date-picker-input"
                    :class="{ 'date-range-active': isScheduleDateRangeActive }"
                    placeholder="Select date range"
                    autocomplete="off"
                  />
                </div>
              </div>
              <div class="filter-group">
                <label>Customer Name</label>
                <input 
                  type="text" 
                  v-model="orderFilters.customerName" 
                  placeholder="Search name..." 
                  class="filter-input"
                />
              </div>
           
              <div class="filter-group">
                <label>Phone</label>
                <input 
                  type="text" 
                  v-model="orderFilters.phone" 
                  placeholder="Search phone..." 
                  class="filter-input"
                />
              </div>
              
              <div class="filter-group">
                <label>Status</label>
                <select 
                  v-model="orderFilters.status" 
                  class="filter-input"
                >
                  <option value="">All Statuses</option>
                  <option v-for="status in uniqueStatuses" :key="status" :value="status">
                    {{ status }}
                  </option>
                </select>
              </div>
              
              <div class="filter-group filter-actions">
                <button class="clear-filters-btn" @click="clearFilters">Clear All</button>
              </div>
            </div>
            <div class="filter-summary" v-if="filteredOrders.length !== selectedDay.orders.length">
              Showing {{ filteredOrders.length }} of {{ selectedDay.orders.length }} orders
            </div>
            <div class="filter-summary" v-if="getActiveFiltersCount > 0">
              Active filters: {{ getActiveFiltersCount }}
            </div>
          </div>

          <div class="modal-summary">
            <div class="summary-item">📦 Total Orders: {{ selectedDay.orders.length }}</div>
            <div class="summary-item" v-if="filteredOrders.length !== selectedDay.orders.length">
              🔍 Filtered: {{ filteredOrders.length }}
            </div>
            <div class="summary-item" v-if="orderFilters.requestedDateStart">
              📅 Requested: {{ formatDateRange(orderFilters.requestedDateStart, orderFilters.requestedDateEnd) }}
            </div>
            <div class="summary-item" v-if="orderFilters.scheduleDateStart">
              📅 Scheduled: {{ formatDateRange(orderFilters.scheduleDateStart, orderFilters.scheduleDateEnd) }}
            </div>
            <div class="summary-item" v-if="orderFilters.status">
              📌 Status: {{ orderFilters.status }}
            </div>
          </div>

          <div class="table-container">
            <table class="details-table">
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Requested Delivery Date</th>
                  <th>Schedule Date</th>
                  <th>Customer Name</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(order, idx) in filteredOrders" :key="idx" class="fade-in-row" :style="{ animationDelay: `${idx * 0.02}s` }">
                  <td>{{ order.order_no || '-' }}</td>
                  <td :class="{ 'date-highlight': isDateInRange(order.requested_delivery_date, orderFilters.requestedDateStart, orderFilters.requestedDateEnd) }">
                    {{ order.requested_delivery_date || '-' }}
                  </td>
                  <td :class="{ 'date-highlight': isDateInRange(order.schedule_date, orderFilters.scheduleDateStart, orderFilters.scheduleDateEnd) }">
                    {{ order.schedule_date || '-' }}
                  </td>
                  <td>{{ order.ship_to_name || '-' }}</td>
                  <td>{{ order.ship_to_address || '-' }}</td>
                  <td>{{ order.phone || order.ship_to_phone || '-' }}</td>
                  <td>
                    <span :class="'status-badge status-' + (order.status || '').toLowerCase().replace(/ /g, '-')">
                      {{ order.status || '-' }}
                    </span>
                  </td>
                </tr>
                <tr v-if="filteredOrders.length === 0" class="no-results-row">
                  <td colspan="8">No orders match the current filters</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div v-else-if="!selectedDay.loadingOrders && !selectedDay.ordersError" class="no-details">
          No order information available for this date
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'

const Lightpick = require('lightpick')
import 'lightpick/css/lightpick.css'

export default {
  name: "DeliveryCalendar",
  data() {
    return {
      packet: {},
      loading: false,
      deliveryData: null,
      apiError: null,
      selectedMonth: moment().format("YYYY-MM"),
      selectedMonthDisplay: moment().format("MMMM YYYY"),
      location_id: 90,
      location_name: '',
      location_options: [{ value: 90, text: 'ALL LOCATIONS' }],
      selectedDay: null,
      monthPicker: null,
      requestedDatePickers: {},
      scheduleDatePickers: {},
      orderFilters: {
        invoiceId: '',
        orderNo: '',
        requestedDate: '',
        requestedDateStart: '',
        requestedDateEnd: '',
        scheduleDate: '',
        scheduleDateStart: '',
        scheduleDateEnd: '',
        customerName: '',
        address: '',
        phone: '',
        status: ''
      },
      // Loading animation properties
      apiLoading: false,
      loadingMessage: 'Loading Calendar Data',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      apiCallStack: 0,
      tips: [
        "📅 Loading calendar data...",
        "🗺️ Fetching delivery schedules...",
        "🚚 Retrieving trip information...",
        "📦 Processing delivery counts...",
        "📍 Geocoding addresses...",
        "🔄 Syncing with server...",
        "✨ Organizing calendar view...",
        "📊 Preparing statistics..."
      ]
    }
  },
  props: {
    selectedLocationId: {
        type: Number,
        default: null
    },
    locationName: {
        type: String,
        default: ''
    }
  },
  computed: {
    calendarDays() {
      if (!this.deliveryData || !this.deliveryData.deliveries_by_date || !this.selectedMonth) return []

      const [year, month] = this.selectedMonth.split('-').map(Number)
      
      const firstDayOfMonth = new Date(year, month - 1, 1)
      const startDayOfWeek = firstDayOfMonth.getDay()
      
      const startDate = new Date(year, month - 1, 1 - startDayOfWeek)
      
      const lastDayOfMonth = new Date(year, month, 0)
      const endDayOfWeek = lastDayOfMonth.getDay()
      const endDate = new Date(year, month, 6 - endDayOfWeek)
      
      const days = []
      let currentDate = new Date(startDate)
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      
      while (currentDate <= endDate) {
        const yearNum = currentDate.getFullYear()
        const monthNum = currentDate.getMonth() + 1
        const dayNum = currentDate.getDate()
        const dateStr = `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
        
        const info = this.deliveryData.deliveries_by_date[dateStr] || {}
        
        days.push({
          date: dateStr,
          day: dayNum,
          isCurrentMonth: monthNum === month,
          isToday: dateStr === todayStr,
          deliveryCount: info.count || 0,
          trips: info.trips || 0,
          unassigned: info.unassigned || 0,
          invalidAddresses: info.invalid_addresses || 0,
          details: info.details || []
        })
        
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      return days
    },
    
    filteredOrders() {
      if (!this.selectedDay || !this.selectedDay.orders) return []
      
      const filters = this.orderFilters
      return this.selectedDay.orders.filter(order => {
        if (filters.invoiceId) {
          const invoiceVal = (order.invoice_id || order.invoice_no || '').toString().toLowerCase()
          if (!invoiceVal.includes(filters.invoiceId.toLowerCase())) return false
        }
        
        if (filters.orderNo) {
          const orderNoVal = (order.order_no || '').toString().toLowerCase()
          if (!orderNoVal.includes(filters.orderNo.toLowerCase())) return false
        }
        
        if (filters.requestedDateStart && filters.requestedDateEnd) {
          const reqDateVal = (order.requested_delivery_date || '')
          if (reqDateVal < filters.requestedDateStart || reqDateVal > filters.requestedDateEnd) {
            return false
          }
        } else if (filters.requestedDateStart) {
          const reqDateVal = (order.requested_delivery_date || '')
          if (reqDateVal !== filters.requestedDateStart) return false
        }
        
        if (filters.scheduleDateStart && filters.scheduleDateEnd) {
          const schDateVal = (order.schedule_date || '')
          if (schDateVal < filters.scheduleDateStart || schDateVal > filters.scheduleDateEnd) {
            return false
          }
        } else if (filters.scheduleDateStart) {
          const schDateVal = (order.schedule_date || '')
          if (schDateVal !== filters.scheduleDateStart) return false
        }
        
        if (filters.customerName) {
          const nameVal = (order.ship_to_name || '').toLowerCase()
          if (!nameVal.includes(filters.customerName.toLowerCase())) return false
        }
        
        if (filters.address) {
          const addressVal = (order.ship_to_address || '').toLowerCase()
          if (!addressVal.includes(filters.address.toLowerCase())) return false
        }
        
        if (filters.phone) {
          const phoneVal = (order.phone || order.ship_to_phone || '').toString().toLowerCase()
          if (!phoneVal.includes(filters.phone.toLowerCase())) return false
        }
        
        if (filters.status) {
          const statusVal = (order.status || '').toLowerCase()
          if (statusVal !== filters.status.toLowerCase()) return false
        }
        
        return true
      })
    },
    
    uniqueStatuses() {
      if (!this.selectedDay || !this.selectedDay.orders) return []
      const statuses = new Set()
      this.selectedDay.orders.forEach(order => {
        if (order.status && order.status.trim()) {
          statuses.add(order.status)
        }
      })
      return Array.from(statuses).sort()
    },
    
    isRequestedDateRangeActive() {
      return !!(this.orderFilters.requestedDateStart && this.orderFilters.requestedDateEnd)
    },
    
    isScheduleDateRangeActive() {
      return !!(this.orderFilters.scheduleDateStart && this.orderFilters.scheduleDateEnd)
    },
    
    getActiveFiltersCount() {
      let count = 0
      if (this.orderFilters.invoiceId) count++
      if (this.orderFilters.orderNo) count++
      if (this.orderFilters.requestedDateStart) count++
      if (this.orderFilters.scheduleDateStart) count++
      if (this.orderFilters.customerName) count++
      if (this.orderFilters.address) count++
      if (this.orderFilters.phone) count++
      if (this.orderFilters.status) count++
      return count
    },
    
    currentTip() {
      return this.tips[this.currentTipIndex] || this.tips[0]
    }
  },
  watch: {
    selectedLocationId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log("Location changed from prop, refreshing calendar...")
          this.fetchDeliveryCounts()
        }
      },
      immediate: true
    },
    locationName: {
      handler(newVal) {
        if (newVal) {
          this.location_name = newVal
        }
      },
      immediate: true
    }
  },
  methods: {
    refreshCalendar() {
      console.log("Manual refresh triggered")
      this.fetchDeliveryCounts()
    },
    
    getDayOfWeek(dateString) {
      if (!dateString) return '';
      
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      console.log(`Calculating day of week for local date: ${date.toString()}`);
      
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    },
    
    onMonthChange() {
      this.selectedMonthDisplay = moment(this.selectedMonth).format("MMMM YYYY")
      this.fetchDeliveryCounts()
    },
    
    formatMonthYear(dateStr) {
      return moment(dateStr).format('MMMM YYYY')
    },
    
    formatDate(dateStr) {
      return moment(dateStr).format('MMMM D, YYYY')
    },
    
    formatDateRange(start, end) {
      if (!start) return ''
      if (start === end) return moment(start).format('MMM D, YYYY')
      return `${moment(start).format('MMM D')} - ${moment(end).format('MMM D, YYYY')}`
    },
    
    isDateInRange(date, start, end) {
      if (!date) return false
      if (start && end) {
        return date >= start && date <= end
      }
      if (start) {
        return date === start
      }
      return false
    },
    
    closeModal() {
      this.selectedDay = null
      this.clearFilters()
      this.destroyDatePickers()
    },
    
    clearFilters() {
      this.orderFilters = {
        invoiceId: '',
        orderNo: '',
        requestedDate: '',
        requestedDateStart: '',
        requestedDateEnd: '',
        scheduleDate: '',
        scheduleDateStart: '',
        scheduleDateEnd: '',
        customerName: '',
        address: '',
        phone: '',
        status: ''
      }
      if (this.selectedDay && this.requestedDatePickers[this.selectedDay.date]) {
        this.requestedDatePickers[this.selectedDay.date].setDate(null)
      }
      if (this.selectedDay && this.scheduleDatePickers[this.selectedDay.date]) {
        this.scheduleDatePickers[this.selectedDay.date].setDate(null)
      }
    },
    
    destroyDatePickers() {
      if (this.selectedDay && this.requestedDatePickers[this.selectedDay.date]) {
        this.requestedDatePickers[this.selectedDay.date].destroy()
        delete this.requestedDatePickers[this.selectedDay.date]
      }
      if (this.selectedDay && this.scheduleDatePickers[this.selectedDay.date]) {
        this.scheduleDatePickers[this.selectedDay.date].destroy()
        delete this.scheduleDatePickers[this.selectedDay.date]
      }
    },
    
    initModalDatePickers() {
      if (!this.selectedDay) return
      
      const that = this
      const requestedPickerId = `requested-date-picker-${this.selectedDay.date}`
      const schedulePickerId = `schedule-date-picker-${this.selectedDay.date}`
      
      this.requestedDatePickers[this.selectedDay.date] = new Lightpick({
        field: document.getElementById(requestedPickerId),
        singleDate: false,
        repick: true,
        format: 'YYYY-MM-DD',
        dropdowns: {
          years: true,
          months: true
        },
        onSelect: function(start, end) {
          if (start && end) {
            that.orderFilters.requestedDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.requestedDateEnd = end.format('YYYY-MM-DD')
            that.orderFilters.requestedDate = `${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}`
          } else if (start) {
            that.orderFilters.requestedDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.requestedDateEnd = start.format('YYYY-MM-DD')
            that.orderFilters.requestedDate = start.format('YYYY-MM-DD')
          } else {
            that.orderFilters.requestedDateStart = ''
            that.orderFilters.requestedDateEnd = ''
            that.orderFilters.requestedDate = ''
          }
        }
      })
      
      this.scheduleDatePickers[this.selectedDay.date] = new Lightpick({
        field: document.getElementById(schedulePickerId),
        singleDate: false,
        repick: true,
        format: 'YYYY-MM-DD',
        dropdowns: {
          years: true,
          months: true
        },
        onSelect: function(start, end) {
          if (start && end) {
            that.orderFilters.scheduleDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.scheduleDateEnd = end.format('YYYY-MM-DD')
            that.orderFilters.scheduleDate = `${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}`
          } else if (start) {
            that.orderFilters.scheduleDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.scheduleDateEnd = start.format('YYYY-MM-DD')
            that.orderFilters.scheduleDate = start.format('YYYY-MM-DD')
          } else {
            that.orderFilters.scheduleDateStart = ''
            that.orderFilters.scheduleDateEnd = ''
            that.orderFilters.scheduleDate = ''
          }
        }
      })
      
      if (this.orderFilters.requestedDateStart && this.orderFilters.requestedDateEnd) {
        if (this.orderFilters.requestedDateStart === this.orderFilters.requestedDateEnd) {
          this.requestedDatePickers[this.selectedDay.date].setDate(moment(this.orderFilters.requestedDateStart))
        } else {
          this.requestedDatePickers[this.selectedDay.date].setDateRange(
            moment(this.orderFilters.requestedDateStart),
            moment(this.orderFilters.requestedDateEnd)
          )
        }
      } else if (this.orderFilters.requestedDateStart) {
        this.requestedDatePickers[this.selectedDay.date].setDate(moment(this.orderFilters.requestedDateStart))
      }
      
      if (this.orderFilters.scheduleDateStart && this.orderFilters.scheduleDateEnd) {
        if (this.orderFilters.scheduleDateStart === this.orderFilters.scheduleDateEnd) {
          this.scheduleDatePickers[this.selectedDay.date].setDate(moment(this.orderFilters.scheduleDateStart))
        } else {
          this.scheduleDatePickers[this.selectedDay.date].setDateRange(
            moment(this.orderFilters.scheduleDateStart),
            moment(this.orderFilters.scheduleDateEnd)
          )
        }
      } else if (this.orderFilters.scheduleDateStart) {
        this.scheduleDatePickers[this.selectedDay.date].setDate(moment(this.orderFilters.scheduleDateStart))
      }
    },
    
    onLocationChange() {
      this.location_name = this.getLocationName(this.location_id)
      this.fetchDeliveryCounts()
    },
    
    getLocationName(locationId) {
      if (!locationId) return 'N/A'
      if (this.packet.locationsdict && this.packet.locationsdict[locationId]) {
        return this.packet.locationsdict[locationId]
      }
      return `Location ${locationId}`
    },
    
    async showDeliveryDetails(day) {
      this.selectedDay = {
        date: day.date,
        deliveryCount: day.deliveryCount,
        trips: day.trips,
        unassigned: day.unassigned,
        invalidAddresses: day.invalidAddresses,
        orders: [],
        loadingOrders: true,
        ordersError: null
      }
      
      this.clearFilters()
      
      await new Promise(resolve => setTimeout(resolve, 800))
      
      try {
        let locationId = this.selectedLocationId || this.location_id
        let locationNameForUrl = this.locationName || this.location_name
        
        let url = `/api/sales/envio_orders_by_date?company_id=90&date=${day.date}&type=home_delivery`
        
        if (locationId && locationId !== 90) {
          url += `&location_id=${locationId}`
          if (locationNameForUrl) {
            url += `&location_name=${encodeURIComponent(locationNameForUrl)}`
          }
        }
        
        console.log("Fetching orders for date:", day.date, url)
        const response = await Requestor.getAsync(url)
        
        console.log("Full response:", response)
        
        let ordersArray = null
        
        if (response.data && response.data.result && Array.isArray(response.data.result)) {
          ordersArray = response.data.result
        }
        else if (response.data && Array.isArray(response.data)) {
          ordersArray = response.data
        }
        else if (Array.isArray(response)) {
          ordersArray = response
        }
        else if (response.result && Array.isArray(response.result)) {
          ordersArray = response.result
        }
        else if (response.data && response.data.data && response.data.data.result && Array.isArray(response.data.data.result)) {
          ordersArray = response.data.data.result
        }
        
        if (ordersArray && ordersArray.length > 0) {
          this.selectedDay.orders = ordersArray
          this.selectedDay.loadingOrders = false
          console.log(`✅ Loaded ${ordersArray.length} orders for ${day.date}`, ordersArray)
        } else {
          this.selectedDay.orders = []
          this.selectedDay.loadingOrders = false
          this.selectedDay.ordersError = null
          console.log(`No orders found for ${day.date}`)
        }
        
        this.$nextTick(() => {
          this.initModalDatePickers()
        })
      } catch (err) {
        console.error("Error fetching orders:", err)
        this.selectedDay.loadingOrders = false
        this.selectedDay.ordersError = err.message || "Network error - please check your connection"
        this.selectedDay.orders = []
      }
    },
    
    // Loading animation methods
    startApiLoading(message = 'Loading Calendar Data') {
      this.apiCallStack++
      if (!this.apiLoading) {
        this.startLoadingAnimation(message)
      } else {
        this.loadingMessage = message
      }
    },
    
    stopApiLoading() {
      this.apiCallStack--
      if (this.apiCallStack <= 0 && this.apiLoading) {
        this.stopLoadingAnimation(true)
      }
    },
    
    startLoadingAnimation(message = 'Loading Calendar Data') {
      this.apiLoading = true
      this.loadingMessage = message
      this.loadingProgress = 0
      this.currentTipIndex = 0
      
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10
          if (this.loadingProgress > 90) this.loadingProgress = 90
        }
      }, 300)
      
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++
        }
      }, 2000)
      
      let messages = ['Loading Calendar Data', 'Fetching Deliveries', 'Processing Calendar', 'Updating Stats', 'Finalizing']
      let msgIndex = 0
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++
          this.loadingMessage = messages[msgIndex]
        }
      }, 1800)
    },
    
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval)
      if (this.tipInterval) clearInterval(this.tipInterval)
      if (this.msgInterval) clearInterval(this.msgInterval)
      
      if (success) {
        this.loadingProgress = 100
        setTimeout(() => {
          this.apiLoading = false
        }, 500)
      } else {
        this.apiLoading = false
      }
    },
    
    async fetchDeliveryCounts() {
      if (!this.selectedMonth) {
        this.selectedMonth = moment().format("YYYY-MM")
      }
      
      this.startApiLoading('Loading Calendar Data')
      this.loading = true
      this.apiError = null
      this.deliveryData = null
      
      try {
        let locationId = this.selectedLocationId || this.location_id
        let locationNameForUrl = this.locationName || this.location_name
        
        let url = `/api/sales/envio_delivery_counts?company_id=90&month=${this.selectedMonth}&type=home_delivery`
        
        if (locationId && locationId !== 90) {
          url += `&location_id=${locationId}`
          if (locationNameForUrl) {
            url += `&location_name=${encodeURIComponent(locationNameForUrl)}`
          }
        }
        
        console.log("Fetching with location:", {locationId, locationNameForUrl, url})
        const response = await Requestor.getAsync(url)
        
        let resultArray = null
        
        if (response.data && response.data.data && response.data.data.data && response.data.data.data.result) {
          resultArray = response.data.data.data.result
        } 
        else if (response.data && response.data.data && response.data.data.result) {
          resultArray = response.data.data.result
        }
        else if (response.data && response.data.result) {
          resultArray = response.data.result
        }
        else if (response.result) {
          resultArray = response.result
        }
        else if (response.data && Array.isArray(response.data)) {
          resultArray = response.data
        }
        
        if (resultArray && Array.isArray(resultArray) && resultArray.length > 0) {
          const deliveriesByDate = {}
          
          for (const item of resultArray) {
            if (item.date) {
              deliveriesByDate[item.date] = {
                count: item.deliveries_by_date || 0,
                trips: item.trips_by_date || 0,
                unassigned: item.unassigned_orders_by_date || 0,
                invalid_addresses: item.invalid_address_orders || 0,
                details: []
              }
            }
          }
          
          this.deliveryData = { deliveries_by_date: deliveriesByDate }
          console.log("✅ Data loaded successfully:", Object.keys(deliveriesByDate).length, "days with data")
          this.apiError = null
        } else {
          this.deliveryData = null
          this.apiError = "No delivery data found for the selected period"
          console.error("No result array found in response:", response.data)
        }
      } catch (err) {
        console.error("Error fetching delivery data:", err)
        this.apiError = err.message || "Network error - please check your connection"
        this.deliveryData = null
      } finally {
        this.loading = false
        this.stopApiLoading()
      }
    },
    
    loadUser() {
      try {
        const packetData = localStorage.packet || '{}'
        const userDetailsData = localStorage.userDetails || '{}'
        
        this.packet = JSON.parse(packetData)
        this.packet.userDetails = JSON.parse(userDetailsData)
        
        if (this.packet.locationsdict) {
          this.location_options = [{ value: 90, text: 'ALL LOCATIONS' }]
          const excluded = ["OUT OF STOCK", "101 WH", "9TH ST ", "ONLINE SALES", "ALL", "FOLCROFT"]
          for (const [id, name] of Object.entries(this.packet.locationsdict)) {
            if (!excluded.includes(name.toUpperCase())) {
              this.location_options.push({ 
                value: parseInt(id), 
                text: name.toUpperCase() 
              })
            }
          }
        }
        
        this.location_name = this.getLocationName(this.location_id)
      } catch (err) {
        console.error("Error loading user:", err)
        this.packet = { userDetails: {} }
      }
    }
  },
  created() {
    this.loadUser()
    this.fetchDeliveryCounts()
  },
  mounted() {
    document.body.classList.add('calendar-page-loaded')
  },
  beforeDestroy() {
    this.destroyDatePickers()
    document.body.classList.remove('calendar-page-loaded')
    this.stopLoadingAnimation()
  }
}
</script>

<style scoped>
/* Glass Theme Background - Same as Delivery Operations */
.delivery-calendar-wrapper.glass-theme {
  position: relative;
  background: transparent !important;
  min-height: 100vh;
  padding: 20px;
}

.delivery-calendar-wrapper.glass-theme::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(10px);
  opacity: 0.5;
  z-index: -2;
  transform: scale(1.1);
}

/* Page Entrance */
@keyframes fadeInPage {
  from {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.fade-in-page {
  animation: fadeInPage 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

/* Calendar Day Stagger */
@keyframes fadeInStagger {
  from {
    opacity: 0;
    transform: scale(0.94);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.fade-in-stagger {
  opacity: 0;
  animation: fadeInStagger 0.45s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
}

/* Modal Slide */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(48px);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.slide-in-up {
  animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

/* Table Row Fade */
@keyframes fadeInRow {
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.fade-in-row {
  opacity: 0;
  animation: fadeInRow 0.28s cubic-bezier(0.2, 0.9, 0.4, 1) forwards;
}

/* Loading Overlay Styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay-enter-active {
  animation: fadeIn 0.3s ease;
}

.loading-overlay-leave-active {
  animation: fadeOut 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  max-width: 450px;
  width: 90%;
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 32px;
}

.loading-ring {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 32px;
  font-weight: bold;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

.loading-logo i {
  font-size: 40px;
  background: linear-gradient(135deg, #4299e1, #48bb78);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 24px;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.3rem;
  margin-bottom: 8px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 6px;
  height: 6px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
}

.loading-progress {
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-tips {
  margin-top: 20px;
}

.tip-text {
  color: #718096;
  font-size: 0.9rem;
  animation: tipFade 0.5s ease;
}

@keyframes tipFade {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.spinner-sm {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 6px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============================================ */
/* ENHANCED TRUCK LOADING ANIMATION */
/* ============================================ */

.truck-loading-container {
  position: relative;
  width: 100%;
  background: linear-gradient(135deg, rgba(240, 244, 248, 0.9), rgba(226, 232, 240, 0.9));
  backdrop-filter: blur(5px);
  border-radius: 12px;
  overflow: hidden;
}

.truck-crossing-animation {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(226, 232, 240, 0.8), rgba(248, 250, 252, 0.9));
}

/* Loading Dock / Warehouse on LEFT side */
.loading-dock {
  position: absolute;
  bottom: 35px;
  left: 15px;
  width: 110px;
  height: 75px;
  background: linear-gradient(145deg, #475569 0%, #334155 100%);
  border-radius: 8px 8px 4px 4px;
  z-index: 5;
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.2);
  border-left: 3px solid #fbbf24;
}

.loading-dock::before {
  content: '🏭';
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 20px;
  opacity: 0.8;
}

.loading-dock::after {
  content: 'LOADING';
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 8px;
  font-weight: bold;
  color: #fbbf24;
  letter-spacing: 1px;
  white-space: nowrap;
}

/* Parcels waiting on dock */
.parcel-on-dock {
  position: absolute;
  bottom: 38px;
  font-size: 20px;
  z-index: 6;
  animation: parcelWait 0.8s ease-in-out infinite;
}

.parcel-on-dock:nth-child(2) {
  left: 60px;
  animation-delay: 0s;
}

.parcel-on-dock:nth-child(3) {
  left: 80px;
  animation-delay: 0.2s;
}

.parcel-on-dock:nth-child(4) {
  left: 40px;
  animation-delay: 0.4s;
}

@keyframes parcelWait {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-3px) scale(1.05);
  }
}

/* Flying parcels being loaded */
.flying-parcel {
  position: absolute;
  bottom: 48px;
  font-size: 18px;
  z-index: 20;
  pointer-events: none;
  opacity: 0;
}

.flying-parcel-1 {
  left: 50px;
  animation: flyToTruck 0.8s ease-in-out forwards;
  animation-delay: 2.5s;
}

.flying-parcel-2 {
  left: 65px;
  animation: flyToTruck 0.7s ease-in-out forwards;
  animation-delay: 2.8s;
}

.flying-parcel-3 {
  left: 40px;
  animation: flyToTruck 0.9s ease-in-out forwards;
  animation-delay: 3.1s;
}

@keyframes flyToTruck {
  0% {
    opacity: 1;
    transform: translateX(0) translateY(0) rotate(0deg);
  }
  30% {
    transform: translateX(40px) translateY(-15px) rotate(15deg);
  }
  60% {
    transform: translateX(100px) translateY(-5px) rotate(-10deg);
  }
  100% {
    opacity: 0;
    transform: translateX(180px) translateY(0) rotate(0deg);
  }
}

/* Truck moving */
.truck-moving {
  position: absolute;
  bottom: 35px;
  left: -200px;
  z-index: 10;
  animation: driveSequence 8s cubic-bezier(0.45, 0, 0.2, 1) forwards;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
}

@keyframes driveSequence {
  0% {
    left: -200px;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  20% {
    left: 30px;
  }
  25% {
    left: 30px;
  }
  30% {
    left: 30px;
  }
  55% {
    left: 30px;
  }
  65% {
    left: 160px;
  }
  75% {
    left: 290px;
  }
  85% {
    left: 420px;
    opacity: 1;
  }
  95% {
    left: 550px;
    opacity: 1;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}

/* Truck facing RIGHT */
.truck-facing-right {
  transform: scaleX(1);
}

/* Truck Body */
.truck {
  position: relative;
  width: 160px;
  height: 65px;
}

.truck-body {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 110px;
  height: 42px;
  background: linear-gradient(145deg, #1e40af 0%, #1e3a8a 100%);
  border-radius: 8px 8px 6px 6px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.truck-body::before {
  content: '';
  position: absolute;
  top: -14px;
  left: 14px;
  width: 82px;
  height: 14px;
  background: linear-gradient(145deg, #2563eb 0%, #1d4ed8 100%);
  border-radius: 6px 6px 0 0;
}

.truck-body::after {
  content: '📦';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 20px;
  opacity: 0;
  animation: parcelAppear 0.5s ease-out forwards;
  animation-delay: 3.5s;
}

@keyframes parcelAppear {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }
  to {
    opacity: 0.9;
    transform: translate(-50%, -50%) scale(1);
  }
}

/* Parcels inside truck */
.parcel-inside {
  position: absolute;
  font-size: 12px;
  opacity: 0;
}

.parcel-inside-1 {
  top: 15px;
  left: 25px;
  animation: fadeInParcel 0.3s ease-out forwards;
  animation-delay: 3.6s;
}

.parcel-inside-2 {
  top: 15px;
  left: 45px;
  animation: fadeInParcel 0.3s ease-out forwards;
  animation-delay: 3.8s;
}

.parcel-inside-3 {
  top: 25px;
  left: 35px;
  animation: fadeInParcel 0.3s ease-out forwards;
  animation-delay: 4.0s;
}

@keyframes fadeInParcel {
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Truck Cabin */
.truck-cabin {
  position: absolute;
  bottom: 0;
  right: 12px;
  width: 50px;
  height: 52px;
  background: linear-gradient(145deg, #1e3a8a 0%, #172554 100%);
  border-radius: 8px 8px 6px 6px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.truck-window {
  position: absolute;
  top: 10px;
  right: 8px;
  width: 22px;
  height: 24px;
  background: linear-gradient(145deg, #a5f3fc 0%, #67e8f9 100%);
  border-radius: 5px;
  z-index: 3;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.4);
  animation: glassReflection 2.5s ease-in-out infinite;
}

.truck-window::after {
  content: '👨';
  position: absolute;
  top: 2px;
  right: 4px;
  font-size: 14px;
  opacity: 0.7;
}

@keyframes glassReflection {
  0%, 100% {
    opacity: 0.9;
  }
  50% {
    opacity: 1;
    box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.7);
  }
}

.headlight {
  position: absolute;
  bottom: 12px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #fef08a;
  border-radius: 50%;
  z-index: 3;
  box-shadow: 0 0 8px #fde047;
  animation: beamPulse 1.2s ease-in-out infinite;
}

.headlight::before {
  content: '';
  position: absolute;
  top: 2px;
  right: -20px;
  width: 30px;
  height: 4px;
  background: linear-gradient(90deg, transparent, #fef08a);
  border-radius: 2px;
  opacity: 0.6;
  animation: beamGlow 0.8s ease-in-out infinite;
}

@keyframes beamPulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
    box-shadow: 0 0 12px #fde047;
  }
}

@keyframes beamGlow {
  0%, 100% {
    opacity: 0.4;
    width: 30px;
  }
  50% {
    opacity: 0.8;
    width: 40px;
  }
}

.truck-wheel {
  position: absolute;
  bottom: -8px;
  width: 20px;
  height: 20px;
  background: #111827;
  border-radius: 50%;
  z-index: 4;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.truck-wheel::before {
  content: '';
  position: absolute;
  top: 6px;
  left: 6px;
  width: 8px;
  height: 8px;
  background: #4b5563;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px #374151;
}

.wheel-front {
  right: 12px;
  animation: wheelSpin 0.5s linear infinite;
}

.wheel-rear {
  left: 20px;
  animation: wheelSpin 0.5s linear infinite;
}

@keyframes wheelSpin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.exhaust {
  position: absolute;
  bottom: 15px;
  left: 10px;
  width: 6px;
  height: 6px;
  background: #94a3b8;
  border-radius: 50%;
  opacity: 0;
  animation: smokePuff 0.8s ease-out infinite;
}

.exhaust:nth-child(2) {
  animation-delay: 0.3s;
  left: 5px;
}

.exhaust:nth-child(3) {
  animation-delay: 0.6s;
  left: 0px;
}

@keyframes smokePuff {
  0% {
    opacity: 0.5;
    transform: translateX(0) scale(0.5);
  }
  100% {
    opacity: 0;
    transform: translateX(-15px) scale(1.5);
  }
}

.loading-text {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.96);
  padding: 8px 20px;
  border-radius: 40px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  z-index: 20;
  white-space: nowrap;
}

.loading-icon {
  font-size: 16px;
  animation: iconFloat 1.2s ease-in-out infinite;
}

@keyframes iconFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

/* ===== Calendar Core Styles with Glass Effect ===== */
.delivery-calendar-wrapper {
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 15px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.calendar-header h3 {
  margin: 0;
  font-size: 28px;
  font-weight: 500;
  letter-spacing: 1px;
  color: #1e293b;
}

.filters {
  display: flex;
  gap: 12px;
  align-items: center;
}

.month-picker-input,
.location-select,
.refresh-btn {
  padding: 10px 16px;
  font-size: 13px;
  border: 1px solid rgba(203, 213, 225, 0.5);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #1e293b;
}

.month-picker-input {
  width: 180px;
}

.month-picker-input:hover,
.location-select:hover {
  background: rgba(255, 255, 255, 1);
  border-color: #94a3b8;
}

.refresh-btn {
  background: linear-gradient(135deg, rgba(54, 64, 80, 0.9), rgba(54, 64, 80, 0.95));
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 6px;
}

.refresh-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(54, 64, 80, 1), rgba(54, 64, 80, 1));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.date-picker-container {
  position: relative;
}

.date-picker-input {
  cursor: pointer;
  width: 180px;
}

.date-picker-input.date-range-active {
  border-color: #008ed5;
  background-color: rgba(0, 142, 213, 0.1);
  box-shadow: 0 0 0 2px rgba(0, 142, 213, 0.1);
}

.calendar {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.calendar-title {
  background: linear-gradient(135deg, rgba(0, 142, 213, 0.85), rgba(0, 100, 150, 0.85));
  backdrop-filter: blur(5px);
  color: #ffffff;
  text-align: center;
  padding: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.calendar-month-title {
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #ffffff;
}

.calendar-location-title {
  font-size: 15px;
  margin-top: 8px;
  color: #ffffff;
  font-weight: 500;
  opacity: 0.9;
}

.calendar-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.weekdays {
  background: rgba(250, 252, 255, 0.8);
  backdrop-filter: blur(5px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  font-weight: 700;
  text-align: center;
}

.weekdays div {
  padding: 16px 8px;
  font-size: 13px;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-day {
  min-height: 140px;
  padding: 12px;
  border-right: 1px solid rgba(237, 242, 247, 0.5);
  border-bottom: 1px solid rgba(237, 242, 247, 0.5);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.calendar-day:nth-child(7n) {
  border-right: none;
}

.calendar-day.other-month {
  background: rgba(254, 254, 254, 0.5);
}

.calendar-day.other-month .day-number {
  color: #94a3b8;
}

.calendar-day.today {
  background: rgba(254, 252, 232, 0.9);
  border-left: 3px solid #eab308;
}

.calendar-day.has-deliveries {
  background: rgba(255, 255, 255, 0.8);
}

.calendar-day:hover {
  background: rgba(0, 142, 213, 0.15);
  transform: scale(1.01);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  z-index: 2;
  border-color: rgba(203, 213, 225, 0.8);
}

.day-number {
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 12px;
  color: #1e293b;
}

.calendar-day.today .day-number {
  color: #ca8a04;
  font-weight: 800;
}

.day-stats {
  font-size: 16px;
}

.stat-row {
  padding: 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.no-deliveries {
  color: #cbd5e1;
  font-size: 24px;
  text-align: center;
  margin-top: 32px;
  font-weight: 400;
}

.loading, .error, .no-data {
  text-align: center;
  padding: 60px 24px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #475569;
  font-size: 15px;
}

.error {
  color: #b91c1c;
  background: rgba(254, 242, 242, 0.9);
  border-color: rgba(254, 202, 202, 0.5);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.glass-modal {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-content {
  max-width: 92vw;
  max-height: 88vh;
  overflow: auto;
  min-width: 800px;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(248, 250, 252, 0.9);
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  flex-shrink: 0;
}

.modal-header h4 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
}

.close-btn {
  background: rgba(241, 245, 249, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.5);
  font-size: 26px;
  cursor: pointer;
  color: #475569;
  padding: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
  transform: rotate(90deg);
}

.filter-bar {
  padding: 16px 20px;
  background: rgba(254, 254, 254, 0.8);
  border-bottom: 1px solid rgba(237, 242, 247, 0.5);
  flex-shrink: 0;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.filter-group {
  flex: 1;
  min-width: 140px;
}

.filter-group label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-bottom: 4px;
}

.filter-input {
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.2s;
  color: #1e293b;
}

.filter-input:focus {
  outline: none;
  border-color: #008ed5;
  box-shadow: 0 0 0 3px rgba(0, 142, 213, 0.1);
  background: rgba(255, 255, 255, 1);
}

.filter-input::placeholder {
  color: #94a3b8;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  min-width: 100px;
  flex: 0.5;
}

.clear-filters-btn {
  width: 100%;
  padding: 8px 12px;
  background: rgba(241, 245, 249, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-filters-btn:hover {
  background: rgba(226, 232, 240, 1);
  color: #1e293b;
}

.filter-summary {
  margin-top: 12px;
  font-size: 12px;
  color: #008ed5;
  font-weight: 500;
  text-align: right;
  padding-top: 8px;
  border-top: 1px dashed rgba(226, 232, 240, 0.8);
}

.modal-summary {
  display: flex;
  gap: 16px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(237, 242, 247, 0.5);
  flex-wrap: wrap;
  flex-shrink: 0;
}

.summary-item {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  padding: 4px 12px;
  background: rgba(248, 250, 252, 0.8);
  border-radius: 30px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-new {
  background-color: rgba(219, 234, 254, 0.8);
  color: #1e40af;
}

.status-paid {
  background-color: rgba(220, 252, 231, 0.8);
  color: #166534;
}

.status-completed,
.status-finished {
  background-color: rgba(209, 250, 229, 0.8);
  color: #065f46;
}

.status-unable-to-complete {
  background-color: rgba(254, 226, 226, 0.8);
  color: #991b1b;
}

.status-pending {
  background-color: rgba(254, 243, 199, 0.8);
  color: #92400e;
}

.status-shipped {
  background-color: rgba(224, 231, 255, 0.8);
  color: #3730a3;
}

.status-delivered {
  background-color: rgba(209, 250, 229, 0.8);
  color: #065f46;
}

.status-cancelled {
  background-color: rgba(254, 226, 226, 0.8);
  color: #991b1b;
}

.status-returned {
  background-color: rgba(254, 243, 199, 0.8);
  color: #92400e;
}

.status-badge:not([class*="status-"]) {
  background-color: rgba(226, 232, 240, 0.8);
  color: #475569;
}

.table-container {
  flex: 1;
  overflow: auto;
  max-height: calc(88vh - 280px);
}

.details-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.details-table th, 
.details-table td {
  border: 1px solid rgba(233, 237, 242, 0.8);
  padding: 12px 14px;
  text-align: left;
  vertical-align: top;
  color: #1e293b;
}

.details-table th {
  background: rgba(248, 250, 252, 0.9);
  font-weight: 700;
  color: #334155;
  position: sticky;
  top: 0;
  z-index: 10;
}

.details-table tr:hover {
  background: rgba(250, 252, 255, 0.8);
  transition: background 0.2s ease;
}

.date-highlight {
  background-color: rgba(254, 243, 199, 0.8);
  font-weight: 600;
  color: #92400e;
}

.no-results-row td {
  text-align: center;
  padding: 40px;
  color: #64748b;
  font-style: italic;
}

.no-details {
  padding: 48px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
}

.loading-orders, .error-orders {
  padding: 48px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
}

.error-orders {
  color: #b91c1c;
  background: rgba(254, 242, 242, 0.9);
}

@media (max-width: 1000px) {
  .delivery-calendar-wrapper { padding: 16px; }
  .calendar-day { min-height: 120px; padding: 8px; }
  .stat-row { font-size: 10px; gap: 4px; }
  .day-number { font-size: 16px; }
  .weekdays div { font-size: 11px; padding: 12px 4px; }
  .modal-content { min-width: 90vw; width: 95vw; }
  .filter-group { min-width: 120px; }
  .month-picker-input { width: 100%; }
}

@media (max-width: 768px) {
  .delivery-calendar-wrapper { padding: 12px; }
  .calendar-header {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
  }
  .calendar-header h3 { font-size: 24px; text-align: center; }
  .filters {
    justify-content: stretch;
    flex-direction: column;
  }
  .filters .month-picker-input,
  .filters .location-select,
  .filters .refresh-btn { width: 100%; }
  .calendar { overflow-x: auto; }
  .calendar-body { min-width: 700px; }
  .calendar-month-title { font-size: 20px; }
  .calendar-day { min-height: 100px; }
  .stat-row { font-size: 9px; white-space: normal; word-break: break-word; }
  .no-deliveries { font-size: 20px; margin-top: 20px; }
  .modal-content { min-width: 95vw; max-width: 98vw; }
  .modal-header h4 { font-size: 18px; }
  .summary-item { font-size: 12px; }
  .details-table th, .details-table td { padding: 8px 10px; font-size: 11px; }
  .filter-row { flex-direction: column; gap: 10px; }
  .filter-group { min-width: auto; }
  .filter-actions { min-width: auto; }
  .clear-filters-btn { width: 100%; }
  .loading-text { font-size: 13px; white-space: nowrap; }
  .truck-crossing-animation { height: 160px; }
  .truck { transform: scale(0.8); }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsDeliveryCalendar.vue